import React, { FC, useState } from 'react';
import classNames from 'classnames/bind';
import SlaOptionCard from './components/SlaOptionCard';
import { SlaCodeLabels, SlaOption, SlaViewModel } from './types';
import styles from './index.pcss';

const cx = classNames.bind(styles);

export interface DeliverySLAProps {
  viewModel: SlaViewModel;
  loading?: boolean;
  error?: boolean;
  activePincode?: string;
  /** open the change-pincode flow */
  onChangePincode?: () => void;
  /** fired when the user picks a different SLA option (multi only) */
  onSelectOption?: (option: SlaOption) => void;
  footerNote?: string;
  className?: string;
  /** luxe theme */
  luxe?: boolean;
  /** hide the pincode / change-pincode header row (host supplies its own heading) */
  hideHeader?: boolean;
  /**
   * Host-supplied heading, rendered only once there is a response to head —
   * i.e. with the options or the error, never over the loading skeleton.
   */
  title?: React.ReactNode;
  /** SLA-code → copy map from priorityDeliverySLPTagsConfig (PLP_BUCKET) */
  slaCodeLabels?: SlaCodeLabels;
  /** Cart only — suffix the amount with "/ pc" (prices there are per piece). */
  perPiece?: boolean;
}

const DEFAULT_FOOTER = 'Delivery time may vary based on items in your bag';

/**
 * Pure, redux-free Delivery SLA UI for desktop. Renders Single or Multi SLA from
 * a mapped view model. Reusable across desktop PDP and Cart — each page supplies
 * its own data (via a connected container) and callbacks.
 */
const DeliverySLA: FC<DeliverySLAProps> = ({
  viewModel,
  loading = false,
  error = false,
  activePincode,
  onChangePincode,
  onSelectOption,
  footerNote = DEFAULT_FOOTER,
  className = '',
  luxe = false,
  hideHeader = false,
  slaCodeLabels,
  title = null,
  perPiece = false
}) => {
  const { options, isMulti, isEmpty } = viewModel;

  const initialSelected = (
    options.find(o => o.isSelected && o.serviceable) || options.find(o => o.serviceable)
  )?.priceGroupId;

  /**
   * The manual pick is scoped to the view model it was made on. Each scopedView
   * response (size / pincode change) hands over a new view model, so the API's
   * `isSelected` takes over again — a priceGroupId can repeat across sizes, so
   * keying off the derived id alone would leave the old card highlighted while
   * redux (selectedSlaOption, set on V2 SUCCESS) already moved on.
   */
  const [userSelection, setUserSelection] = useState<{
    token: SlaViewModel;
    id?: string;
  } | null>(null);

  const selectedId =
    userSelection &&
    userSelection.token === viewModel &&
    options.some(o => o.priceGroupId === userSelection.id)
      ? userSelection.id
      : initialSelected;

  const handleSelect = (option: SlaOption) => {
    setUserSelection({ token: viewModel, id: option.priceGroupId });
    onSelectOption?.(option);
  };

  const renderHeader = () => {
    if (hideHeader) return null;
    return (
      <div className={cx('sla-header')}>
        {activePincode && <span className={cx('sla-pincode')}>{activePincode}</span>}
        {onChangePincode && (
          <button type="button" className={cx('sla-change-pincode')} onClick={onChangePincode}>
            Change Pincode
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={cx('sla', className, { luxe })} data-testid="sla-loading">
        {renderHeader()}
        <div className={cx('sla-skeleton')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cx('sla', className, { luxe })}>
        {title}
        {renderHeader()}
        <p className={cx('sla-error')}>Unable to fetch delivery details. Please try again.</p>
      </div>
    );
  }

  if (isEmpty) return null;

  return (
    <div className={cx('sla', className, { luxe })} role={isMulti ? 'radiogroup' : undefined}>
      {title}
      {renderHeader()}
      <div className={cx('sla-list')}>
        {options.map(option => (
          <SlaOptionCard
            key={option.priceGroupId || option.priceType}
            option={option}
            selectable={isMulti}
            showBadge={isMulti}
            selected={option.priceGroupId === selectedId}
            onSelect={handleSelect}
            htmlName="sla-option-group"
            luxe={luxe}
            slaCodeLabels={slaCodeLabels}
            perPiece={perPiece}
            activePincode={activePincode}
          />
        ))}
      </div>
      {footerNote && <p className={cx('sla-footer')}>{footerNote}</p>}
    </div>
  );
};

export default DeliverySLA;
